/**
 * The brush.
 *
 * It owns no pixels. It used to own the one canvas that was simultaneously the
 * artwork and the viewer's texture, which was right when a design was a single
 * surface and wrong the moment layers existed — a brush that owns the artwork
 * can only ever paint on one thing.
 *
 * So it draws into whatever context `target()` hands back, resolved per stroke
 * rather than captured once. Switching the active layer needs no rewiring, and
 * a target of null (a placement is selected, which is not paintable in template
 * coordinates) makes the brush a no-op instead of an error.
 *
 * Strokes are clipped to the regions plus their gutters, which is Edge Integrity
 * stage 0 done for free at input time rather than as a cleanup pass at export.
 * Paint cannot land in the 54.9% of the canvas that never renders.
 */
import getStroke from "perfect-freehand";
import { computeCoverage, coverageStep, type RegionBounds } from "../../shared/brush";
import { CANVAS, type RegionId } from "../../shared/format";
import type { MirrorMap } from "../../shared/mirror-map";
import { partGroup, partSide } from "../../shared/parts";
import { type PositionMap, partAt, positionAt } from "../../shared/position-map";
import { REGIONS, regionAt } from "../../shared/regions";
import { allowsAt, EMPTY, paintRects, type Selection } from "../../shared/selection";
import { mirrorBoundsFor, mirrorStroke, type Pixels } from "../../shared/symmetry";
import {
  edgeFalloff,
  perStampAlpha,
  pressureScale,
  resample,
  type StampPoint,
  snapToAngle,
  spacingFor,
} from "./brush-stamp";
import { landsHere } from "./footprint";

/** Everything needed to ask where a brush lands in 3D. */
export interface Surface {
  map: PositionMap;
  bounds: RegionBounds[];
  /** World units per texel. */
  unit: number;
}

// Type-only, so this and layers.ts referencing each other costs nothing at
// runtime — both imports erase at build time.
import type { StrokeMode } from "./layers";

export interface DirtyRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PaintOptions {
  /**
   * Where the finished stroke goes. Called on commit, so the active layer can
   * change between strokes without anything here being rebound.
   */
  target: () => CanvasRenderingContext2D | null;
  /** Texture pixels. */
  size?: number;
  colour?: string;
  /** 0..1, applied once when the stroke commits. */
  opacity?: number;
  /**
   * 0..1. 1 draws the filled outline; below 1 stamps a gradient tip.
   *
   * Defaults to 1, so every existing caller keeps the brush it had.
   */
  hardness?: number;
  /** 0..1, coverage laid down over one brush diameter of travel. */
  flow?: number;
  /**
   * How the stroke combines with the layer, resolved at commit time.
   *
   * **Must be the same function the stack's stroke overlay reads.** The preview
   * and the commit are two different code paths, and an editor whose preview
   * disagrees with its result is worse than one that cannot preview — you would
   * only discover it after lifting the pen. See `strokeCompositeMode` in
   * layers.ts, which exists to be that single source of truth.
   */
  compositeMode?: () => StrokeMode;
  /**
   * True while the stroke should be a straight line from where it began.
   *
   * Read per point rather than captured at `begin`, because the modifier is
   * pressed and released mid-drag — that is the whole idiom. A stroke can start
   * freehand, straighten while the key is held, and go back to freehand.
   */
  straight?: () => boolean;
  /**
   * The area that changed, coalesced per frame.
   *
   * The rect and nothing else. This used to hand over a set of touched regions
   * as well, sampled from the region under the rect's CENTRE — which is wrong
   * in the two cases that matter and was read by nobody: the only consumer
   * recomputes from the geometry, because a recomposite has no pointer to
   * sample and the answer has to come from the area either way.
   *
   * Wrong how: with symmetry on, the rect spans a stroke and its mirror, and
   * the midpoint of a rect from one arm to the other lands in the gutter
   * between the strips, where `regionAt` returns null — so the set came back
   * EMPTY for exactly the strokes that touched the most faces. A stroke
   * straddling a fold reported one of the two faces it painted.
   *
   * Deleted rather than corrected: a second, subtly different answer to
   * "which faces did this touch" is a trap whether or not it is right today.
   */
  onDirty?: (rect: DirtyRect) => void;
  /**
   * Live symmetry. Return the mirror map to mirror each stroke as it commits,
   * or null for none — read per stroke, so the toggle takes effect on the next
   * one without anything being rebound.
   *
   * Deliberately a getter for a map rather than a boolean: the map is built per
   * body and per garment and costs ~200ms, so the caller owns its lifetime and
   * caching. See shared/mirror-map.ts.
   */
  mirror?: () => MirrorMap | null;
  /**
   * The body under the template, so a stroke can cross a fold.
   *
   * The template is a folded-up body, and a stroke that stops dead at a face's
   * edge is the artifact — on the body that edge is a corner you can run a
   * brush over. With this, a disc straddling one lands on both faces because it
   * genuinely covers both.
   *
   * Read per stroke. Returning null paints in template space alone, which is
   * what happens below 1024px where no body is loaded.
   */
  surface?: () => Surface | null;
  /**
   * Which faces this stroke may touch. Empty — the default — means all of them.
   *
   * Read once per stroke rather than per frame, so changing the selection
   * mid-drag cannot alter a stroke already under way. See shared/selection.ts.
   */
  selection?: () => Selection;
  /**
   * Let a stroke carry onto a different part of the body — arm to torso, torso
   * to leg. Off unless asked for.
   *
   * A fold and a JOINT are not the same thing, and the brush cannot tell them
   * apart on distance alone. Within one part the surface really is continuous:
   * the chest wraps over the shoulder, an R15 forearm continues past the elbow,
   * and a stroke that stopped there would be the artifact. Between parts there
   * is a seam the body can move, and worse, parts TOUCH — the classic arm's
   * inner face sits 0.0026 studs off the torso's side, so a ball centred on one
   * contains the other.
   *
   * Measured: painting the inside of the arm mid-limb put 440 of 879 texels on
   * the torso. Half the stroke, on a face hidden behind the arm, with nothing
   * on screen to say so.
   */
  crossParts?: () => boolean;
  /**
   * One completed stroke, with the affected rect before and after it landed.
   *
   * Fires once per stroke, from `commit()`. Omit it and the two `getImageData`
   * readbacks are skipped entirely — nothing pays for undo it is not using.
   */
  onEdit?: (rect: DirtyRect, before: ImageData, after: ImageData) => void;
}

interface Point {
  x: number;
  y: number;
  pressure: number;
}

/**
 * Stamp spacing as a fraction of brush diameter, before the flow floor.
 *
 * 0.18 rather than the ~0.1 the literature suggests, because the literature is
 * describing per-stamp alpha and we normalise. Measured: tighter than about
 * 0.15 the per-stamp alpha of a light flow drops under four levels in 255 and
 * the stroke goes grainy, and `spacingFor` has to widen it back anyway. See
 * docs/decisions/soft-brush.md.
 */
const BASE_SPACING = 0.18;

/**
 * `#rgb` or `#rrggbb` plus an alpha, as a canvas colour string.
 *
 * Local rather than imported: the colour picker's parser is bound up with its
 * HSV model, and this needs six lines and no dependency. Falls back to opaque
 * black on anything unrecognised, which is visible rather than silent.
 */
function rgbaOf(hex: string, alpha: number): string {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) h = (h[0] ?? "") + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = Number.parseInt(h, 16);
  if (h.length !== 6 || Number.isNaN(n)) return `rgba(0, 0, 0, ${alpha})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** Region rects grown by the gutter — the only places paint may land. */
/**
 * Region rects grown by the gutter — the only places paint may land.
 *
 * Narrowed by the face selection when there is one. With the gutter, matching
 * `allowsAt`: a stroke on a selected face must have exactly the shape it would
 * have with no selection, and clipping to the bare rect would shave two pixels
 * off every edge of it.
 */
function paintableMask(selection: Selection = EMPTY, keepPartOf: RegionId | null = null): Path2D {
  /*
    The part the stroke started on, when it must not leave it.

    This is the clip for the stroke's OWN outline, drawn flat on the template —
    and until it took a part, a stroke could not leave a *region* but could
    happily leave a *limb*. The atlas puts the two limb columns 27px apart
    (rightLimb.front ends at x=281, leftLimb.front starts at 308) and the brush
    goes up to 120px wide, so a wide stroke near that gap simply painted in
    both columns: one dab on the right sleeve, an identical one on the left.

    Reported twice as paint "going from one limb to another", and the reason
    the first fix did not catch it is that `landsHere` guards a different path.
    That one filters the texels a stroke wraps onto across a *fold*, computed
    from the body in 3D. This is the flat outline, and nothing here ever
    consulted "Paint across joints" at all.

    Matched on the region id's `<part>` prefix, the same rule fill.ts uses for
    a whole-part fill: `torso.*` stays on the torso, `leftLimb.*` on that
    sleeve. Folds within one part share the prefix, so wrapping — which the
    tooltip promises always happens — is untouched.
  */
  const path = new Path2D();
  /*
    `paintRects` rather than the arithmetic inline, because a layer restricted
    to some faces clips its composite to the same rects. A stroke allowed to
    land where the composite then clips it away is paint that is in the file
    and cannot be seen.

    Fed the part-limited face list rather than the raw selection, so the two
    restrictions compose: the faces the user chose, narrowed to the part the
    stroke began on.
  */
  const allowed = strokeRegions(selection, keepPartOf);
  /*
    Nothing allowed means nothing painted, and it has to be said here.
    An empty Selection means the OPPOSITE to `paintRects` — "no restriction",
    every face — so delegating this case would open the mask up completely at
    exactly the moment it should be shut.
  */
  if (allowed.length === 0) return path;
  for (const r of paintRects(new Set(allowed))) path.rect(r.x, r.y, r.w, r.h);
  return path;
}

/**
 * The faces a stroke starting on `keepPartOf` is allowed to land in.
 *
 * Separated from the Path2D so the rule can be tested: `Path2D` does not exist
 * outside a browser, and this rule is where the bug was.
 *
 * `keepPartOf` null means no part limit — either "Paint across joints" is on,
 * or the stroke began off the regions entirely.
 */
export function strokeRegions(
  selection: Selection,
  keepPartOf: RegionId | null = null,
): RegionId[] {
  const part = keepPartOf ? keepPartOf.split(".")[0] : null;
  return (Object.keys(REGIONS) as RegionId[]).filter((id) => {
    if (selection.size > 0 && !selection.has(id)) return false;
    if (part && id.split(".")[0] !== part) return false;
    return true;
  });
}

export function createPainter(opts: PaintOptions) {
  /*
    Rebuilt per stroke, because the selection can change between them.

    Eighteen rects, so the cost is nothing; the value is that a stroke's clip is
    fixed at the moment the pointer goes down. Reading the selection per frame
    instead would let a click on the face picker mid-drag reshape a stroke
    already in progress.
  */
  let mask = paintableMask();
  let size = opts.size ?? 24;
  let colour = opts.colour ?? "#E2231A";
  let opacity = opts.opacity ?? 1;
  /**
   * 1 is the filled outline this file has always drawn; below 1 switches to a
   * stamped tip with a gradient edge. Two paths rather than one that does both
   * badly — the outline is the better hard brush and cannot be soft, and the
   * stamped tip is the only soft one and is a worse hard brush.
   */
  let hardness = opts.hardness ?? 1;
  /** Coverage laid down over one brush diameter of travel. See brush-stamp.ts. */
  let flow = opts.flow ?? 1;

  // Stroke-local stamped state, all reset by begin().
  let stampCursor = 0;
  let stampPending = 0;
  let stampSpacingPx = 1;
  let stampAlpha = 1;
  let stampGradient: CanvasGradient | null = null;

  let points: Point[] = [];
  let drawing = false;

  /**
   * The wet layer.
   *
   * Redrawing the whole stroke every frame is what keeps the taper continuous —
   * incremental segments visibly band. Doing it onto the artwork was the bug:
   * measured, 120 pointer events produced 122 full-stroke fills, every one
   * stacked on the destination. Quadratic, edges compounding, and the stroke
   * genuinely applied 122 times.
   *
   * So the redraws land here, on a canvas cleared each frame, and the artwork
   * receives exactly one composite when the stroke ends. That is also what makes
   * three other things possible: opacity applied once instead of 122 times, an
   * eraser that does not expand on every pass, and a stroke that is a single
   * operation for undo to hold.
   */
  const wet = document.createElement("canvas");
  wet.width = CANVAS.width;
  wet.height = CANVAS.height;
  const maybeWetCtx = wet.getContext("2d");
  if (!maybeWetCtx) throw new Error("2D context unavailable");
  const wetCtx: CanvasRenderingContext2D = maybeWetCtx;

  /** Area of `wet` currently dirty, so clearing stays cheap on long strokes. */
  let wetBounds: DirtyRect | null = null;

  /*
    Texels this stroke reaches on faces OTHER than the one under the pointer,
    accumulated as the stroke grows.

    Computed once per point and kept, rather than recomputed per frame:
    `renderStroke` redraws the whole stroke on every pointer event, and a 3D
    coverage scan per point per frame would be the stutter this file's
    coarse-then-fine brush machinery exists to avoid. Points only ever get
    appended, so a cached answer never goes stale.

    A Set because a slow drag revisits the same texels many times, and drawing
    each one once a frame instead of forty times is most of the cost.
  */
  /*
    Texel index -> how far into the brush it was, as a fraction of the radius.

    A Set was enough while every crossed texel took the same alpha, and that
    was the bug: the bleed came out as a flat slab across the fold instead of
    following the brush's own edge. computeCoverage has always returned the
    distance; nothing read it.

    The smallest fraction wins, because a stroke passes a texel many times and
    the closest approach is the one that decides how much paint it should have.
  */
  const crossing = new Map<number, number>();
  /** The selection this stroke began under. See `selection` in PaintOptions. */
  let selection: Selection = EMPTY;
  let crossBounds: DirtyRect | null = null;
  /*
    Crossings added since the last render.

    The outline path redraws the whole stroke each frame and can simply replay
    the whole set. The stamped path never redraws anything, so it needs to know
    which texels are new — replaying the set would deposit another stamp's worth
    of alpha on every one of them, every frame, and the seam bleed would darken
    into a solid line while the stroke it belongs to stayed soft.
  */
  let freshCrossings: number[] = [];
  /** Where the coverage scan last ran. Reset per stroke. See addCrossing. */
  let lastCoverAt: { x: number; y: number } | null = null;
  /**
   * The freehand points, kept while a straight line is being previewed.
   *
   * Straightening is not destructive: let go of the modifier and the stroke you
   * had drawn is still there. Without this, tapping the key mid-stroke would
   * silently discard everything before it.
   */
  let freehand: Point[] | null = null;
  /** True while the last render drew a straight line rather than the path. */
  let wasStraight = false;

  /**
   * The composite mode this stroke started with, held until it ends.
   *
   * Resolved once at `begin()` rather than read at commit, because the tool can
   * change mid-drag — a keyboard shortcut fires while the pointer is still
   * down, and a stroke that previewed as paint would commit as an erase. The
   * preview reads this same captured value, so the two cannot diverge partway
   * through a stroke either.
   */
  let strokeMode: StrokeMode = "source-over";

  // Coalesce to one notification per frame. A pointermove can fire far more
  // often than the display refreshes, and the viewer uploads per frame anyway.
  let pending: DirtyRect | null = null;
  let frame = 0;

  function markDirty(rect: DirtyRect) {
    pending = pending
      ? {
          x: Math.min(pending.x, rect.x),
          y: Math.min(pending.y, rect.y),
          w: Math.max(pending.x + pending.w, rect.x + rect.w) - Math.min(pending.x, rect.x),
          h: Math.max(pending.y + pending.h, rect.y + rect.h) - Math.min(pending.y, rect.y),
        }
      : rect;

    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      if (pending) opts.onDirty?.(pending);
      pending = null;
    });
  }

  /** Render the current point buffer onto the wet layer, from scratch. */
  /**
   * Note where this point's brush lands beyond its own face.
   *
   * Skipped entirely unless the disc can actually reach an edge, which is a
   * cheap rect test and false for almost every stroke — a brush in the middle
   * of the chest costs nothing at all.
   */
  function addCrossing(x: number, y: number, force = false) {
    const surface = opts.surface?.();
    if (!surface) return;
    /*
      Throttled by distance travelled.

      The scan is O(radius squared) and ran on every pointer event — measured at
      3ms an event for a 120px brush, which is 178ms of every second at 60
      events and the lag people reported. Consecutive points are one to three
      pixels apart and the covered set barely moves over that, so most of those
      scans were recomputing the previous answer.

      Throttling by distance rather than by frame is what makes it scale: the
      step is a quarter of the radius, so a big brush — where each scan is
      dearest — is also sampled least often. See coverageStep for why a quarter
      leaves no gap worth seeing.

      Forced at the two points that are not samples of a continuing path: the
      first, which has nothing to compare against, and the last, whose bleed
      would otherwise stop wherever the final sample happened to land.
    */
    if (!force && lastCoverAt) {
      const step = coverageStep(size / 2);
      const dx = x - lastCoverAt.x;
      const dy = y - lastCoverAt.y;
      if (dx * dx + dy * dy < step * step) return;
    }
    lastCoverAt = { x, y };
    const own = regionAt(Math.round(x), Math.round(y));
    if (!own) return;
    const rect = REGIONS[own];
    const radius = size / 2;
    const reaches =
      x - rect.x < radius ||
      rect.x + rect.w - x < radius ||
      y - rect.y < radius ||
      rect.y + rect.h - y < radius;
    if (!reaches) return;

    const at = positionAt(surface.map, Math.round(x), Math.round(y));
    if (!at) return;
    const ownPart = partAt(surface.map, Math.round(x), Math.round(y));
    const ownGroup = ownPart ? partGroup(ownPart) : null;
    // Both arms are group "arms", so without the side a stroke crosses to the
    // opposite limb even with "Paint across joints" off.
    const ownSide = ownPart ? partSide(ownPart) : null;
    const crossParts = opts.crossParts?.() ?? false;
    // The same product computeCoverage compares against internally, so a
    // returned distance divided by it lands in 0..1 across the brush.
    const radiusWorld = radius * surface.unit;
    for (const t of computeCoverage(surface.map, surface.bounds, at, radius, surface.unit)) {
      /*
        One rule, shared with the preview that draws it.

        These checks used to live here alone, which meant the hover preview had
        to reimplement them — and a preview that disagrees with the paint is
        worse than none, because it teaches a wrong model of the tool and the
        only way to find out is to have already ruined a design.
      */
      if (
        !landsHere(t, {
          map: surface.map,
          ownRegion: own,
          ownGroup,
          ownSide,
          crossParts,
          selection,
        })
      ) {
        continue;
      }
      const texel = t.y * CANVAS.width + t.x;
      // Normalised so the falloff is independent of brush size and of the
      // world units the map happens to be in.
      const reach = radiusWorld > 0 ? t.distance / radiusWorld : 0;
      const seen = crossing.get(texel);
      if (seen === undefined || reach < seen) {
        crossing.set(texel, reach);
        // Queued again when it gets closer: the stamped path never redraws, so
        // a texel first caught at the rim would otherwise keep the faint alpha
        // it had there even after the brush passed straight over it.
        freshCrossings.push(texel);
      }
      crossBounds = crossBounds
        ? {
            x: Math.min(crossBounds.x, t.x),
            y: Math.min(crossBounds.y, t.y),
            w: Math.max(crossBounds.x + crossBounds.w, t.x + 1) - Math.min(crossBounds.x, t.x),
            h: Math.max(crossBounds.y + crossBounds.h, t.y + 1) - Math.min(crossBounds.y, t.y),
          }
        : { x: t.x, y: t.y, w: 1, h: 1 };
    }
  }

  /** The stroke's own rect, widened by wherever it crossed a fold. */
  function strokeBounds(): DirtyRect {
    const own = boundsOf(points, size);
    if (!crossBounds) return own;
    const x = Math.min(own.x, crossBounds.x);
    const y = Math.min(own.y, crossBounds.y);
    return {
      x,
      y,
      w: Math.max(own.x + own.w, crossBounds.x + crossBounds.w) - x,
      h: Math.max(own.y + own.h, crossBounds.y + crossBounds.h) - y,
    };
  }

  const isSoft = () => hardness < 1;

  /**
   * The tip, built once per stroke and drawn translated.
   *
   * `createRadialGradient` bakes its coordinates in, so a naive implementation
   * rebuilds one per stamp — hundreds per stroke, and the profile everyone who
   * writes this discovers late. Built at the origin instead and moved into
   * place with a transform, which makes it a per-stroke constant.
   */
  function buildStampGradient(): CanvasGradient | null {
    const r = size / 2;
    if (r <= 0) return null;
    // A gradient whose stops coincide is degenerate, and hardness reaches 1
    // from a slider. The outline path owns that end of the range anyway.
    const inner = r * Math.min(hardness, 0.99);
    const g = wetCtx.createRadialGradient(0, 0, inner, 0, 0, r);
    g.addColorStop(0, rgbaOf(colour, stampAlpha));
    g.addColorStop(1, rgbaOf(colour, 0));
    return g;
  }

  /**
   * Stamp the span added since the last frame. Never redraws.
   *
   * The outline path clears and re-renders the whole stroke every frame, which
   * is right for a single opaque fill and wrong the moment stamps carry alpha:
   * re-stamping a span multiplies it, so a stroke would darken wherever the
   * pointer lingered — the artefact the wet layer exists to prevent, arriving
   * by another door.
   *
   * `resample` carries its leftover distance between calls, so the stamps land
   * in the same places whether the events arrived in ones or twenties.
   */
  function renderStamped() {
    if (points.length === 0) return;
    const ctx = wetCtx;
    const batch = points.slice(stampCursor);
    const { stamps, pending } = resample(batch as StampPoint[], stampSpacingPx, stampPending);
    stampPending = pending;
    // The next batch reopens on this one's last point, so the segment across
    // the boundary is walked exactly once.
    stampCursor = Math.max(0, points.length - 1);

    if (stamps.length > 0 || freshCrossings.length > 0) {
      const r = size / 2;
      ctx.save();
      ctx.clip(mask);
      if (stampGradient) {
        ctx.fillStyle = stampGradient;
        for (const s of stamps) {
          ctx.save();
          // Pressure scales the stamp rather than the gradient, so the tip
          // stays a per-stroke constant. 0.5 is neutral — see pressureScale.
          ctx.globalAlpha = pressureScale(s.pressure);
          ctx.translate(s.x, s.y);
          ctx.fillRect(-r, -r, r * 2, r * 2);
          ctx.restore();
        }
      }
      /*
        Seam bleed, shaded like the brush that made it.

        One texel at a time, each at the strength the brush would have laid
        down at that distance — the same curve the gradient draws for the
        stroke's own body. Painting them all at stampAlpha, which is what this
        did, put a flat hard-edged slab across the fold.
      */
      if (freshCrossings.length > 0) {
        ctx.fillStyle = colour;
        for (const i of freshCrossings) {
          const cx = i % CANVAS.width;
          ctx.globalAlpha = stampAlpha * edgeFalloff(crossing.get(i) ?? 1, hardness);
          ctx.fillRect(cx, (i - cx) / CANVAS.width, 1, 1);
        }
        freshCrossings = [];
      }
      ctx.restore();
    }

    // Accumulated, never cleared: everything stamped so far is still on the wet
    // layer, so the commit and the undo bounds have to cover all of it.
    wetBounds = strokeBounds();
  }

  /**
   * Replace the point buffer with a straight segment, or put the path back.
   *
   * Returns true when the buffer changed shape enough that the wet layer has to
   * be redrawn from scratch — which the stamped path otherwise never does.
   */
  function applyStraight(): boolean {
    const on = opts.straight?.() ?? false;
    if (!on) {
      if (!freehand) return false;
      // Modifier released: restore what was actually drawn.
      points = freehand;
      freehand = null;
      return true;
    }
    const anchor = (freehand ?? points)[0];
    const tip = points[points.length - 1];
    if (!anchor || !tip) return false;
    if (!freehand) freehand = points;
    const end = snapToAngle(anchor.x, anchor.y, tip.x, tip.y);
    points = [anchor, { x: end.x, y: end.y, pressure: tip.pressure }];
    return true;
  }

  /**
   * Throw away the wet layer and draw the buffer again.
   *
   * The stamped path is incremental by design — it never redraws, because
   * re-stamping a span multiplies its alpha. A rubber-band line breaks that
   * assumption: the segment changes shape on every pointer move, so the old one
   * has to go. Clearing first is what makes redrawing safe, and a straight line
   * is two points, so the cost is trivial.
   */
  function redrawFromScratch() {
    if (wetBounds) {
      wetCtx.clearRect(wetBounds.x, wetBounds.y, wetBounds.w, wetBounds.h);
      wetBounds = null;
    }
    stampCursor = 0;
    stampPending = 0;
    // Every crossing is on the cleared layer, so all of them are new again.
    freshCrossings = [...crossing.keys()];
  }

  const render = () => {
    const reshaped = applyStraight();
    const straightNow = opts.straight?.() ?? false;
    if (isSoft() && (reshaped || straightNow || wasStraight)) redrawFromScratch();
    wasStraight = straightNow;
    return isSoft() ? renderStamped() : renderStroke();
  };

  function renderStroke() {
    if (points.length === 0) return;
    const ctx = wetCtx;

    // Clear only what was drawn last frame. Clearing the full 585x559 every
    // pointer event would trade one kind of per-frame waste for another.
    if (wetBounds) {
      ctx.clearRect(wetBounds.x, wetBounds.y, wetBounds.w, wetBounds.h);
      wetBounds = null;
    }

    const outline = getStroke(
      points.map((p) => [p.x, p.y, p.pressure]),
      {
        size,
        thinning: 0.55,
        smoothing: 0.5,
        streamline: 0.4,
        simulatePressure: false,
      },
    );
    if (outline.length < 3) return;

    ctx.save();
    ctx.clip(mask);
    ctx.fillStyle = colour;
    ctx.beginPath();
    ctx.moveTo(outline[0]?.[0] ?? 0, outline[0]?.[1] ?? 0);
    for (let i = 1; i < outline.length; i++) {
      ctx.lineTo(outline[i]?.[0] ?? 0, outline[i]?.[1] ?? 0);
    }
    ctx.closePath();
    ctx.fill();

    // The same colour, drawn as texels rather than as a path: the crossings are
    // a set of points on another face, not a shape with an outline. Inside the
    // same clip and the same wet layer, so opacity, blend mode, mirroring and
    // undo all treat them as part of the one stroke.
    for (const [i, reach] of crossing) {
      const cx = i % CANVAS.width;
      // Full strength inside a hard brush, so this is a no-op there — but a
      // brush softened to any degree gets a bleed that matches its edge.
      ctx.globalAlpha = edgeFalloff(reach, hardness);
      ctx.fillRect(cx, (i - cx) / CANVAS.width, 1, 1);
    }
    ctx.globalAlpha = 1;
    ctx.restore();

    // Remember what to clear next frame. The whole stroke is redrawn each time,
    // so its full bounds are dirty, not just the newest segment.
    wetBounds = strokeBounds();
  }

  /**
   * Commit the wet layer to the artwork — the one and only write per stroke.
   *
   * Opacity and blend live here rather than in `renderStroke` for the reason the
   * whole wet layer exists: applied per frame they would compound 122 times over
   * a long stroke, which is why the Brush panel's opacity row has never been
   * anything but decoration.
   */
  function commit() {
    const ctx = opts.target();
    if (ctx && wetBounds && wetBounds.w > 0 && wetBounds.h > 0) {
      const r = wetBounds;
      /*
        Undo is captured here and nowhere else, because here is the only place
        the stroke's extent is finally known. At `begin()` there is no stroke
        yet; per dab there is no single edit — that is the whole reason the wet
        layer exists. Two reads of one rect, once per stroke.
      */
      /*
        With symmetry on, the mirror writes somewhere else entirely — the far
        limb, usually — so undo has to cover both or an undo would restore one
        half of the stroke and leave the other. The mirror's rect is not known
        until the stroke has been composited and copied, so the safe bound is
        the whole region pair, taken up front.

        `bounds` is what undo and the readbacks use; `r` stays the stroke's own
        rect and still bounds the composite.
      */
      const mirror = opts.mirror?.() ?? null;
      const bounds = mirror ? mirrorBoundsFor(r, mirror) : r;
      const before = opts.onEdit ? ctx.getImageData(bounds.x, bounds.y, bounds.w, bounds.h) : null;

      /*
        Mirror the STAMP, before compositing — not the result, after.

        The old order composited the stroke, then copied finished texels to
        their partners. That copy REPLACED whatever was on the far side, and a
        brush edge is not binary: measured, a soft-edged stroke landing where
        the mirror side already had artwork punched 72 texels of partial
        transparency into it, worst case 255 down to 40. That is the outline
        people see where strokes overlap.

        Mirroring the wet layer instead means the stroke and its mirror are one
        stamp, composited once. Soft edges blend onto whatever is underneath
        because that is what compositing does, and the eraser mirrors as an
        erase rather than as a copy of an erased result.

        The original reasoning still holds and is why this is not "replaying the
        stroke": replaying would composite twice where the halves meet. Copying
        within the wet layer is a single stamp, and `mirrorStroke`'s
        lower-index-wins rule keeps it that way across the centreline.
      */
      let composite = r;
      if (mirror) {
        const wetView = wetCtx.getImageData(bounds.x, bounds.y, bounds.w, bounds.h);
        // Target and coverage are the same buffer here: the stamp is both what
        // is copied and what says where. `mirrorStroke` reads every source
        // before writing any, so the aliasing is safe by construction.
        const stamp: Pixels = {
          width: bounds.w,
          height: bounds.h,
          data: wetView.data,
          originX: bounds.x,
          originY: bounds.y,
        };
        /*
          The far side obeys the selection too.

          `paintableMask` clips the stroke's own outline and `landsHere` filters
          what carries across a fold — both because a stroke must not reach a
          face somebody excluded. The mirror used to walk past both: measured, a
          stroke confined to `torso.front` put all 564 of its texels onto
          `torso.back` while the back was deselected.

          `selection` is the one captured at `begin()`, so this cannot change
          under a stroke already in flight, and `allowsAt` returns true
          immediately when nothing is selected.
        */
        const written = mirrorStroke(stamp, stamp, r, mirror, {
          allow: (x, y) => allowsAt(selection, x, y),
        });
        if (written) {
          wetCtx.putImageData(wetView, bounds.x, bounds.y);
          composite = bounds;
          /*
            The wet layer now holds paint outside the stroke's own rect, and
            `wetBounds` is what the cleanup below clears. Leaving it at `r` left
            the mirrored half on the wet layer for good: measured, a stroke that
            put 564 texels on the far side left all 564 of them there, and the
            next stroke — whose composite rect spans the same pair — stamped
            them onto the artwork again. A dab that should have left 166 texels
            on the mirror side left 344.
          */
          wetBounds = bounds;
        }
      }

      ctx.save();
      ctx.globalAlpha = opacity;
      // The mode captured at begin(), not re-resolved here: the tool may have
      // changed since the pointer went down.
      ctx.globalCompositeOperation = strokeMode;
      // Clipped to the stroke AND its mirror, which is why `composite` widens
      // when one was written — clipping to the stroke alone would composite the
      // near half and drop the far one entirely.
      ctx.beginPath();
      ctx.rect(composite.x, composite.y, composite.w, composite.h);
      ctx.clip();
      ctx.drawImage(wet, 0, 0);
      ctx.restore();

      if (composite !== r) markDirty(composite);

      if (before) {
        opts.onEdit?.(
          { ...bounds },
          before,
          ctx.getImageData(bounds.x, bounds.y, bounds.w, bounds.h),
        );
      }
    }
    if (wetBounds) {
      wetCtx.clearRect(wetBounds.x, wetBounds.y, wetBounds.w, wetBounds.h);
      wetBounds = null;
    }
  }

  function boundsOf(pts: Point[], pad: number): DirtyRect {
    let x0 = Number.POSITIVE_INFINITY;
    let y0 = Number.POSITIVE_INFINITY;
    let x1 = Number.NEGATIVE_INFINITY;
    let y1 = Number.NEGATIVE_INFINITY;
    for (const p of pts) {
      if (p.x < x0) x0 = p.x;
      if (p.y < y0) y0 = p.y;
      if (p.x > x1) x1 = p.x;
      if (p.y > y1) y1 = p.y;
    }
    return {
      x: Math.max(0, Math.floor(x0 - pad)),
      y: Math.max(0, Math.floor(y0 - pad)),
      w: Math.min(CANVAS.width, Math.ceil(x1 + pad)) - Math.max(0, Math.floor(x0 - pad)),
      h: Math.min(CANVAS.height, Math.ceil(y1 + pad)) - Math.max(0, Math.floor(y0 - pad)),
    };
  }

  return {
    /** The wet layer, for the stack to preview above the layer being drawn on. */
    wetCanvas: wet,
    /** Read as a function by the stack, so the preview never shows a stale value. */
    opacity: () => opacity,
    /**
     * Whether a stroke is currently in flight.
     *
     * The stack uses this to skip the scratch-buffer path when nothing is being
     * drawn, so a reorder or a visibility toggle does not pay for a compositing
     * step that would draw an empty canvas.
     */
    hasStroke: () => wetBounds !== null,
    /**
     * The mode this stroke is committing with.
     *
     * The stack's overlay reads this rather than resolving the mode itself, so
     * the preview and the commit are literally the same value and not merely
     * the same function evaluated twice.
     */
    mode: () => strokeMode,

    setSize(px: number) {
      size = px;
    },
    setColour(hex: string) {
      colour = hex;
    },
    /** 0..1. Below 1 switches to the stamped tip; 1 is the filled outline. */
    setHardness(next: number) {
      hardness = Math.max(0, Math.min(1, next));
    },
    /** 0..1, coverage over one brush diameter of travel. */
    setFlow(next: number) {
      flow = Math.max(0, Math.min(1, next));
    },
    setOpacity(next: number) {
      opacity = Math.max(0, Math.min(1, next));
    },

    begin(x: number, y: number, pressure = 0.5) {
      /*
        Commit anything still in flight before starting.

        This used to reset `points` and let `renderStroke` clear the wet layer,
        which quietly destroyed the previous stroke if it had never been ended —
        the paint was on the wet layer and nowhere else. It is defence in depth
        rather than the fix: a lost pointerup is handled in wirePainting now. But
        the failure mode here is losing work with no error, so the belt is worth
        having alongside the braces.
      */
      if (drawing) {
        render();
        const stranded = strokeBounds();
        commit();
        markDirty(stranded);
      }

      drawing = true;
      strokeMode = opts.compositeMode?.() ?? "source-over";
      selection = opts.selection?.() ?? EMPTY;
      /*
        Fixed at pointer-down, like the selection above and for the same
        reason: which part a stroke belongs to is decided by where it started,
        not by wherever the pointer has wandered to by mid-drag.
      */
      mask = paintableMask(
        selection,
        opts.crossParts?.() ? null : regionAt(Math.round(x), Math.round(y)),
      );
      points = [{ x, y, pressure }];
      crossing.clear();
      crossBounds = null;
      freshCrossings = [];
      lastCoverAt = null;
      /*
        The stamped tip, resolved once for the whole stroke.

        `spacingFor` may widen the requested spacing: per-stamp alpha shrinks as
        spacing tightens, and under about four levels in 255 the rounding error
        patterns rather than averages and the stroke goes grainy — exactly when
        used most gently, which is the shading case. Fewer, better-represented
        stamps carry the same paint.
      */
      stampCursor = 0;
      stampPending = 0;
      const spacingFrac = spacingFor(flow, BASE_SPACING);
      stampSpacingPx = Math.max(0.5, size * spacingFrac);
      stampAlpha = perStampAlpha(flow, spacingFrac);
      stampGradient = isSoft() ? buildStampGradient() : null;
      addCrossing(x, y);
      render();
      markDirty(strokeBounds());
    },

    extend(x: number, y: number, pressure = 0.5) {
      if (!drawing) return;
      points.push({ x, y, pressure });
      addCrossing(x, y);
      render();
      // The whole stroke is re-rendered, so the whole stroke is what changed on
      // the wet layer — reporting only the last segment left the tail of a fast
      // drag unpresented until the next event happened to cover it.
      markDirty(strokeBounds());
    },

    end() {
      if (!drawing) return;
      drawing = false;
      /*
        One last scan at the final point.

        Everything between samples is covered by the discs either side of it,
        but the end of a stroke has nothing after it — without this the bleed
        stops at the last sample and the final pixels do not carry across a
        fold.
      */
      const tip = points[points.length - 1];
      if (tip) addCrossing(tip.x, tip.y, true);
      render();
      const bounds = strokeBounds();
      commit();
      markDirty(bounds);
      points = [];
    },

    /**
     * Throw away the stroke in progress, without committing it.
     *
     * `end()` is the pen lifting; this is the stroke never having counted. The
     * case it exists for is a second finger landing on a tablet: the first was
     * drawing, the gesture is now a pinch, and committing what it had drawn
     * would leave a smear across the artwork every time somebody zoomed. That
     * is the single most common complaint about drawing tools on a touchscreen,
     * and it is entirely this.
     *
     * The wet layer is cleared and the area repainted, so what was on screen
     * mid-stroke does not linger — but nothing reaches the layer and no history
     * entry is written, so there is nothing to undo afterwards either.
     */
    cancel() {
      if (!drawing) return;
      drawing = false;
      const bounds = strokeBounds();
      points = [];
      crossing.clear();
      crossBounds = null;
      freshCrossings = [];
      wetCtx.clearRect(0, 0, wet.width, wet.height);
      wetBounds = null;
      markDirty(bounds);
    },
  };
}

/*
  `setBase` and `setBitmap` used to live here. They belonged to a brush that
  owned the artwork, and both moved to the layer stack — importing now adds a
  layer rather than overwriting whatever was on the one canvas, which is the
  behaviour that stops a live-linked file from wiping the logo sitting above it.
*/

export type Painter = ReturnType<typeof createPainter>;
